import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { map, isEmpty } from 'lodash';
import { Link } from 'react-router-dom';

import { PATH } from '../../../../constants/path';
import { CONTEXT_ROOT, STATIC_ROOT } from '../../../../constants';
import { MOMENTJS_DATE_FORMAT, MOMENTJS_DATE_FORMAT_STANDARD } from '../../../../constants/moment';

import { fetchContactRequestReasonIfNeeded } from '../../../../services/contactrequest/actions';
import { fetchAccountMessagesIfNeeded } from '../../../../services/account/mymessages/actions';

import { toDate } from '../../../../utils/dateUtils';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import SvgComponent from '../../../SvgComponent';
import HeadComponent from '../../../HeadComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';

import './MyMessagesPage.scss';

const DISPLAY_STEP = 5;

const ICON_VALIDATED = 'validation';
const ICON_WAITING = 'pending';

const renderIcon = (icon, width, height) => (
    <SvgComponent
        src={`${STATIC_ROOT}/icon/picto-${icon}.svg`}
        width={width}
        height={height}
        aria-hidden="true"
        focusable="false"
    />
);

const renderMessageState = (icon, text, width, height) => (
    <div className="message-caption-block center-mobile">
        {renderIcon(icon, width, height)}
        <p className="hide-mobile">{text}</p>
    </div>
);

export class MyMessagesPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { index: DISPLAY_STEP };
    }

    componentDidMount() {
        this.props.fetchContactRequestReasonIfNeeded();
        this.props.fetchAccountMessagesIfNeeded();
    }

    renderConditionnalOnMessageState(state) {
        if (!state) return null;

        const { i18n } = this.props;
        if (state.id === 3) {
            return renderMessageState(ICON_VALIDATED, i18n.MESSAGES_CAPTION.DONE, 24, 24);
        } else {
            return renderMessageState(ICON_WAITING, i18n.MESSAGES_CAPTION.WAITING, 24, 24);
        }
    }

    renderConditionnalOnAnswerDate(answerDate) {
        if (!answerDate) return null;
        if (answerDate > 0) {
            return toDate(answerDate, MOMENTJS_DATE_FORMAT);
        }
    }

    renderContactRequestReasonLabel(id) {
        const { UNDEFINED } = this.props.common;
        const reason = this.props.contactrequestreasons.find(e => e.id === id);
        if (isEmpty(reason)) return UNDEFINED;
        return reason.label;
    }

    renderSeeMoreButton() {
        const { i18n, data } = this.props;
        const { index } = this.state;
        const length = Array.isArray(data) ? data.length : 0;

        if (length >= DISPLAY_STEP && index < length) {
            return (
                <div className="messages-button">
                    <button
                        type="button"
                        id="more-messages-button"
                        className="see-more-button"
                        title={i18n.SEE_MORE_MESSAGES}
                        onClick={this.handleSeeMoreClick}>
                        <span className="plus" aria-hidden="true">
                            +
                        </span>
                        <span className="text">{i18n.SEE_MORE_MESSAGES}</span>
                    </button>
                </div>
            );
        }
    }

    handleSeeMoreClick = () => {
        this.setState({
            index: this.state.index + DISPLAY_STEP
        });
    };

    render() {
        const { i18n, data, fetching } = this.props;
        const { index } = this.state;

        const safeData = Array.isArray(data) ? data : [];
        const visibleMessages = safeData.slice(0, index);

        return (
            <div id="p-account-messages" className="container">
                <HeadComponent
                    title={i18n.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${PATH.ACCOUNT_MESSAGES_PATH}`}
                />
                <h2>{i18n.TITLE}</h2>

                <div className="information-div" role="note" id="table-caption">
                    <span aria-hidden="true">
                        <SvgComponent
                            src={`${STATIC_ROOT}/information.svg`}
                            width="20"
                            height="20"
                            aria-hidden="true"
                            focusable="false"
                        />
                    </span>
                    <span>{i18n.TABLE_CAPTION}</span>
                </div>

                {!fetching && visibleMessages.length > 0 && (
                    <table
                        className="messages-table"
                        aria-label={i18n.TABLE_LABEL}
                        aria-describedby="table-caption">
                        <thead>
                            <tr>
                                <th scope="col">
                                    <span className="hide-mobile">{i18n.MESSAGE_DATE_DESKTOP}</span>
                                    <span className="mobile-only">{i18n.MESSAGE_DATE_MOBILE}</span>
                                </th>
                                <th scope="col" className="reason">
                                    <span className="hide-mobile">
                                        {i18n.REQUEST_REASON_DESKTOP}
                                    </span>
                                    <span className="mobile-only">
                                        {i18n.REQUEST_REASON_MOBILE}
                                    </span>
                                </th>
                                <th scope="col" className="state">
                                    <span className="hide-mobile">
                                        {i18n.REQUEST_STATE_DESKTOP}
                                    </span>
                                    <span className="mobile-only">{i18n.REQUEST_STATE_MOBILE}</span>
                                </th>
                                <th scope="col" className="details">
                                    {i18n.DETAILS}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {map(visibleMessages, message => (
                                <tr key={message.id}>
                                    <td>
                                        <span className="hide-mobile">
                                            {toDate(message.requestDate, MOMENTJS_DATE_FORMAT)}
                                        </span>
                                        <span className="mobile-only">
                                            {toDate(
                                                message.requestDate,
                                                MOMENTJS_DATE_FORMAT_STANDARD
                                            )}
                                        </span>
                                    </td>
                                    <td className="reason">
                                        {this.renderContactRequestReasonLabel(
                                            message.contactRequestReasonId
                                        )}
                                    </td>
                                    <td className="picto">
                                        {this.renderConditionnalOnMessageState(message.state)}
                                    </td>
                                    <td className="details">
                                        <Link
                                            to={`${PATH.ACCOUNT_MESSAGES_PATH}/${message.id}`}
                                            title={convertHtmlToReactDomWithParams(
                                                i18n.SHOW_MESSAGE_DETAILS_TITLE,
                                                toDate(message.requestDate, MOMENTJS_DATE_FORMAT),
                                                this.renderContactRequestReasonLabel(
                                                    message.contactRequestReasonId
                                                )
                                            )}>
                                            <span className="hide-mobile">
                                                {i18n.SHOW_MESSAGE_DETAILS_DESKTOP}
                                            </span>
                                            <span className="mobile-only">
                                                {i18n.SHOW_MESSAGE_DETAILS_MOBILE}
                                            </span>
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {(fetching || safeData.length === 0) && (
                    <div
                        className={`container-message empty ${fetching ? 'fetching' : ''}`}
                        role="status"
                        aria-live="polite">
                        {fetching ? <LoadingComponent /> : i18n.NO_MESSAGES}
                    </div>
                )}

                {this.renderSeeMoreButton()}

                <section className="message-caption">
                    <div className="message-caption-block">
                        {renderIcon(ICON_VALIDATED, 24, 24)}
                        <p>{i18n.MESSAGES_CAPTION.DONE}</p>
                    </div>
                    <div className="message-caption-block">
                        {renderIcon(ICON_WAITING, 24, 24)}
                        <p>{i18n.MESSAGES_CAPTION.WAITING}</p>
                    </div>
                </section>
                <section className="message-bottom">
                    {i18n.FOOTER_TEXT}
                    <Link to={PATH.FAQ_PATH}>FAQ</Link> ?&nbsp;
                    <Link to={PATH.CONTACTREQUEST_PATH}>{i18n.CONTACT_CUSTOMER_SERVICE}</Link>.
                </section>
            </div>
        );
    }
}

MyMessagesPage.propTypes = {
    common: PropTypes.object.isRequired,
    i18n: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    contactrequestreasons: PropTypes.array.isRequired
};

function mapStateToProps(state) {
    return {
        common: state.i18n.common,
        i18n: state.i18n.page.ACCOUNT.MESSAGES,
        data: state.account.mymessages.messages || [],
        fetching: state.account.mymessages.fetching,
        contactrequestreasons: state.contactrequest
            ? state.contactrequest.contactrequestreasons
            : []
    };
}

const mapDispatchToProps = {
    fetchContactRequestReasonIfNeeded,
    fetchAccountMessagesIfNeeded
};

export default connect(mapStateToProps, mapDispatchToProps)(MyMessagesPage);



// WEBPACK FOOTER //
// ./src/components/pages/account/mymessages/MyMessagesPage.js